/**
 * User Profile Management API Hooks
 * React hooks for managing user profile data, trip history, exports, and notifications
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import * as React from 'react';
import { adminApiClient } from './adminApiClient';
import { RiderDTO, DriverDTO } from './adminTypes';
import { ApiResponse, PagedData } from './types';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Rider profile with extended statistics
 */
export interface RiderProfileWithStats extends RiderDTO {
  totalTrips?: number;
  completedTrips?: number;
  cancelledTrips?: number;
  totalSpent?: number;
  averageTripFare?: number;
  memberSince?: string;
  lastTripDate?: string;
  frequentDestinations?: string[];
}

/**
 * Driver profile with extended statistics
 */
export interface DriverProfileWithStats extends DriverDTO {
  totalTrips?: number;
  completedTrips?: number;
  cancelledTrips?: number;
  totalEarnings?: number;
  averageRating?: number;
  acceptanceRate?: number;
  onlineHoursThisMonth?: number;
  memberSince?: string;
  lastTripDate?: string;
  peakHours?: string[];
}

/**
 * Trip status types
 */
export type TripStatus = 'PENDING' | 'ACCEPTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'FAILED';

/**
 * Trip history item for riders
 */
export interface RiderTripHistoryItem {
  tripId: number;
  rideId?: number;
  driverId?: number;
  driverName?: string;
  driverPhone?: string;
  driverRating?: number;
  vehicleInfo?: string;
  pickupLocation: string;
  dropoffLocation: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  dropoffLatitude?: number;
  dropoffLongitude?: number;
  fare: number;
  totalFare?: number;
  discountAmount?: number;
  promoCode?: string;
  status: TripStatus;
  paymentMethod?: string;
  requestTime: string;
  startTime?: string;
  endTime?: string;
  duration?: number; // in minutes
  distance?: number; // in km
  cancellationReason?: string;
  cancelledBy?: 'RIDER' | 'DRIVER' | 'SYSTEM';
  ratingGiven?: number;
  feedback?: string;
}

/**
 * Trip history item for drivers
 */
export interface DriverTripHistoryItem {
  tripId: number;
  rideId?: number;
  riderId?: number;
  riderName?: string;
  riderPhone?: string;
  riderRating?: number;
  pickupLocation: string;
  dropoffLocation: string;
  pickupLatitude?: number;
  pickupLongitude?: number;
  dropoffLatitude?: number;
  dropoffLongitude?: number;
  fare: number;
  totalFare?: number;
  driverEarnings?: number;
  commission?: number;
  status: TripStatus;
  paymentMethod?: string;
  requestTime: string;
  startTime?: string;
  endTime?: string;
  duration?: number; // in minutes
  distance?: number; // in km
  cancellationReason?: string;
  cancelledBy?: 'RIDER' | 'DRIVER' | 'SYSTEM';
  ratingReceived?: number;
  feedback?: string;
}

/**
 * Trip history query parameters
 */
export interface TripHistoryParams {
  page?: number;
  size?: number;
  sort?: string[];
  startDate?: string; // ISO 8601 format
  endDate?: string; // ISO 8601 format
  status?: TripStatus | TripStatus[];
}

/**
 * Export format types
 */
export type ExportFormat = 'CSV' | 'PDF' | 'JSON' | 'XLSX';

/**
 * Export data options
 */
export interface ExportDataOptions {
  format: ExportFormat;
  startDate?: string;
  endDate?: string;
  includeProfile?: boolean;
  includeTrips?: boolean;
  includeStats?: boolean;
}

/**
 * Monthly summary request payload
 */
export interface MonthlySummaryRequest {
  driverId: number;
  month: number; // 1-12
  year: number;
  email?: string; // Optional: override driver's registered email
}

/**
 * Monthly summary response
 */
export interface MonthlySummaryResponse {
  success: boolean;
  message: string;
  emailSent?: boolean;
  summaryData?: {
    month: string;
    year: number;
    totalTrips: number;
    completedTrips: number;
    cancelledTrips: number;
    totalEarnings: number;
    onlineHours: number;
    averageRating: number;
    incentives?: number;
    deductions?: number;
    netPayout: number;
  };
}

// ============================================================================
// Rider Profile Hooks
// ============================================================================

/**
 * Hook to fetch detailed rider profile with statistics
 * @param riderId - Rider ID
 * @param options - Hook options
 */
export function useRiderProfile(
  riderId: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: RiderProfileWithStats) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    // Fetch basic profile
    const profileResponse = await adminApiClient.get<ApiResponse<RiderDTO>>(`/riders/${riderId}`);
    const profileData = profileResponse.data || (profileResponse as unknown as RiderDTO);

    // Fetch rider stats (if separate endpoint exists)
    let statsData: Partial<RiderProfileWithStats> = {};
    try {
      const statsResponse = await adminApiClient.get<ApiResponse<{
        totalTrips?: number;
        completedTrips?: number;
        cancelledTrips?: number;
        totalSpent?: number;
        averageTripFare?: number;
        lastTripDate?: string;
      }>>(`/riders/${riderId}/stats`);
      
      if (statsResponse.data) {
        statsData = statsResponse.data;
      }
    } catch {
      // Stats endpoint might not exist, use default values
      statsData = {
        totalTrips: 0,
        completedTrips: 0,
        cancelledTrips: 0,
        totalSpent: 0,
        averageTripFare: 0,
      };
    }

    // Merge profile and stats
    return {
      ...profileData,
      ...statsData,
      memberSince: profileData.signupDate || profileData.createdDate,
    } as RiderProfileWithStats;
  }, [riderId]);

  const query = useQuery<RiderProfileWithStats>({
    queryKey: ['rider-profile', riderId],
    queryFn,
    enabled: options.enabled !== false && !!riderId,
  });

  // Handle callbacks using useEffect pattern
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to fetch trip history for a specific rider
 * @param riderId - Rider ID
 * @param params - Query parameters (pagination, date range, status filter)
 * @param options - Hook options
 */
export function useRiderTripHistory(
  riderId: number,
  params: TripHistoryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<RiderTripHistoryItem>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.status) {
      queryParams.status = Array.isArray(params.status) 
        ? params.status.join(',') 
        : params.status;
    }

    const response = await adminApiClient.get<ApiResponse<PagedData<RiderTripHistoryItem>>>(
      `/riders/${riderId}/trips`,
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    // Fallback: if response is directly the data
    if (Array.isArray(response)) {
      return {
        content: response,
        page: {
          size: response.length,
          number: 0,
          totalElements: response.length,
          totalPages: 1,
        },
        first: true,
        last: true,
        empty: response.length === 0,
      };
    }

    return response as PagedData<RiderTripHistoryItem>;
  }, [riderId, params.page, params.size, params.sort, params.startDate, params.endDate, params.status]);

  const query = useQuery<PagedData<RiderTripHistoryItem>>({
    queryKey: ['rider-trip-history', riderId, params],
    queryFn,
    enabled: options.enabled !== false && !!riderId,
  });

  // Handle callbacks using useEffect pattern
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return query;
}

// ============================================================================
// Driver Profile Hooks
// ============================================================================

/**
 * Hook to fetch detailed driver profile with statistics
 * @param driverId - Driver ID
 * @param options - Hook options
 */
export function useDriverProfile(
  driverId: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverProfileWithStats) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    // Fetch basic profile
    const profileResponse = await adminApiClient.get<ApiResponse<DriverDTO>>(`/drivers/${driverId}`);
    const profileData = profileResponse.data || (profileResponse as unknown as DriverDTO);

    // Fetch driver stats (if separate endpoint exists)
    let statsData: Partial<DriverProfileWithStats> = {};
    try {
      const statsResponse = await adminApiClient.get<ApiResponse<{
        totalTrips?: number;
        completedTrips?: number;
        cancelledTrips?: number;
        totalEarnings?: number;
        averageRating?: number;
        acceptanceRate?: number;
        onlineHoursThisMonth?: number;
        lastTripDate?: string;
      }>>(`/drivers/${driverId}/stats`);
      
      if (statsResponse.data) {
        statsData = statsResponse.data;
      }
    } catch {
      // Stats endpoint might not exist, derive from profile data
      statsData = {
        totalTrips: profileData.totalRidesCompleted || 0,
        totalEarnings: profileData.totalEarnings || 0,
        averageRating: profileData.driverRating || profileData.averageRating || 0,
      };
    }

    // Merge profile and stats
    return {
      ...profileData,
      ...statsData,
      memberSince: profileData.createdDate,
    } as DriverProfileWithStats;
  }, [driverId]);

  const query = useQuery<DriverProfileWithStats>({
    queryKey: ['driver-profile', driverId],
    queryFn,
    enabled: options.enabled !== false && !!driverId,
  });

  // Handle callbacks using useEffect pattern
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return {
    ...query,
    data: query.data,
    loading: query.isLoading || query.isFetching,
    error: query.error,
  };
}

/**
 * Hook to fetch trip history for a specific driver
 * @param driverId - Driver ID
 * @param params - Query parameters (pagination, date range, status filter)
 * @param options - Hook options
 */
export function useDriverTripHistory(
  driverId: number,
  params: TripHistoryParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: PagedData<DriverTripHistoryItem>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryFn = useCallback(async () => {
    const queryParams: Record<string, any> = {};

    if (params.page !== undefined) {
      queryParams.page = params.page;
    }
    if (params.size !== undefined) {
      queryParams.size = params.size;
    }
    if (params.sort && params.sort.length > 0) {
      queryParams.sort = params.sort;
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.status) {
      queryParams.status = Array.isArray(params.status) 
        ? params.status.join(',') 
        : params.status;
    }

    const response = await adminApiClient.get<ApiResponse<PagedData<DriverTripHistoryItem>>>(
      `/drivers/${driverId}/trips`,
      queryParams
    );

    // Handle Response wrapper
    if (response.data) {
      return response.data;
    }

    // Fallback: if response is directly the data
    if (Array.isArray(response)) {
      return {
        content: response,
        page: {
          size: response.length,
          number: 0,
          totalElements: response.length,
          totalPages: 1,
        },
        first: true,
        last: true,
        empty: response.length === 0,
      };
    }

    return response as PagedData<DriverTripHistoryItem>;
  }, [driverId, params.page, params.size, params.sort, params.startDate, params.endDate, params.status]);

  const query = useQuery<PagedData<DriverTripHistoryItem>>({
    queryKey: ['driver-trip-history', driverId, params],
    queryFn,
    enabled: options.enabled !== false && !!driverId,
  });

  // Handle callbacks using useEffect pattern
  React.useEffect(() => {
    if (query.isSuccess && query.data && options.onSuccess) {
      options.onSuccess(query.data);
    }
  }, [query.isSuccess, query.data, options.onSuccess]);

  React.useEffect(() => {
    if (query.isError && query.error && options.onError) {
      options.onError(query.error);
    }
  }, [query.isError, query.error, options.onError]);

  return query;
}

// ============================================================================
// Export Hooks
// ============================================================================

/**
 * Hook to export all rider data (profile + trips) as CSV/PDF/JSON/XLSX
 * @param options - Hook options
 */
export function useExportRiderData(
  options: {
    onSuccess?: (data: Blob, variables: { riderId: number; options: ExportDataOptions }) => void;
    onError?: (error: Error, variables: { riderId: number; options: ExportDataOptions }) => void;
  } = {}
) {
  return useMutation<Blob, Error, { riderId: number; options: ExportDataOptions }>({
    mutationFn: async ({ riderId, options: exportOptions }) => {
      const queryParams: Record<string, any> = {
        format: exportOptions.format,
      };

      if (exportOptions.startDate) {
        queryParams.startDate = exportOptions.startDate;
      }
      if (exportOptions.endDate) {
        queryParams.endDate = exportOptions.endDate;
      }
      if (exportOptions.includeProfile !== undefined) {
        queryParams.includeProfile = exportOptions.includeProfile;
      }
      if (exportOptions.includeTrips !== undefined) {
        queryParams.includeTrips = exportOptions.includeTrips;
      }
      if (exportOptions.includeStats !== undefined) {
        queryParams.includeStats = exportOptions.includeStats;
      }

      const response = await adminApiClient.get<Blob>(
        `/riders/${riderId}/export`,
        queryParams,
        { responseType: 'blob' }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Trigger file download
      const blob = new Blob([data], { 
        type: getContentTypeByFormat(variables.options.format) 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rider-${variables.riderId}-export-${formatDate(new Date())}.${getExtensionByFormat(variables.options.format)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

/**
 * Hook to export all driver data (profile + trips) as CSV/PDF/JSON/XLSX
 * @param options - Hook options
 */
export function useExportDriverData(
  options: {
    onSuccess?: (data: Blob, variables: { driverId: number; options: ExportDataOptions }) => void;
    onError?: (error: Error, variables: { driverId: number; options: ExportDataOptions }) => void;
  } = {}
) {
  return useMutation<Blob, Error, { driverId: number; options: ExportDataOptions }>({
    mutationFn: async ({ driverId, options: exportOptions }) => {
      const queryParams: Record<string, any> = {
        format: exportOptions.format,
      };

      if (exportOptions.startDate) {
        queryParams.startDate = exportOptions.startDate;
      }
      if (exportOptions.endDate) {
        queryParams.endDate = exportOptions.endDate;
      }
      if (exportOptions.includeProfile !== undefined) {
        queryParams.includeProfile = exportOptions.includeProfile;
      }
      if (exportOptions.includeTrips !== undefined) {
        queryParams.includeTrips = exportOptions.includeTrips;
      }
      if (exportOptions.includeStats !== undefined) {
        queryParams.includeStats = exportOptions.includeStats;
      }

      const response = await adminApiClient.get<Blob>(
        `/drivers/${driverId}/export`,
        queryParams,
        { responseType: 'blob' }
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Trigger file download
      const blob = new Blob([data], { 
        type: getContentTypeByFormat(variables.options.format) 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `driver-${variables.driverId}-export-${formatDate(new Date())}.${getExtensionByFormat(variables.options.format)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

// ============================================================================
// Monthly Summary Hook
// ============================================================================

/**
 * Hook to email monthly summary to driver
 * @param options - Hook options
 */
export function useSendMonthlySummary(
  options: {
    onSuccess?: (data: MonthlySummaryResponse, variables: MonthlySummaryRequest) => void;
    onError?: (error: Error, variables: MonthlySummaryRequest) => void;
  } = {}
) {
  const queryClient = useQueryClient();

  return useMutation<MonthlySummaryResponse, Error, MonthlySummaryRequest>({
    mutationFn: async (requestData: MonthlySummaryRequest) => {
      const response = await adminApiClient.post<ApiResponse<MonthlySummaryResponse>>(
        '/drivers/send-monthly-summary',
        requestData
      );
      return response.data || (response as unknown as MonthlySummaryResponse);
    },
    onSuccess: (data, variables) => {
      // Invalidate driver-related queries
      queryClient.invalidateQueries({ queryKey: ['driver-profile', variables.driverId] });
      queryClient.invalidateQueries({ queryKey: ['driver-trip-history', variables.driverId] });
      
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: options.onError,
  });
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get MIME content type by export format
 */
function getContentTypeByFormat(format: ExportFormat): string {
  switch (format) {
    case 'CSV':
      return 'text/csv';
    case 'PDF':
      return 'application/pdf';
    case 'JSON':
      return 'application/json';
    case 'XLSX':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    default:
      return 'application/octet-stream';
  }
}

/**
 * Get file extension by export format
 */
function getExtensionByFormat(format: ExportFormat): string {
  switch (format) {
    case 'CSV':
      return 'csv';
    case 'PDF':
      return 'pdf';
    case 'JSON':
      return 'json';
    case 'XLSX':
      return 'xlsx';
    default:
      return 'bin';
  }
}

/**
 * Format date for filename
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// ============================================================================
// Re-export types for convenience
// ============================================================================

export type {
  RiderDTO,
  DriverDTO,
  ApiResponse,
  PagedData,
};
